import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Cake, ArrowLeft, ArrowRight, Check, CalendarIcon, Minus, Plus, Clock, MapPin, CreditCard, Banknote, ShoppingCart, Trash2, X, PartyPopper } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cakes, setCakes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cart state - multiple items
  const [cart, setCart] = useState([]);
  const [rentalCart, setRentalCart] = useState([]);
  const [selectedCakeToAdd, setSelectedCakeToAdd] = useState(null);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [messageToAdd, setMessageToAdd] = useState("");

  // Form state
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState("");
  const [selectedZone, setSelectedZone] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  
  // Loyalty points state
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [loyaltyChecked, setLoyaltyChecked] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cakesRes, zonesRes, rentalsRes] = await Promise.all([
          axios.get(`${API}/cakes`),
          axios.get(`${API}/delivery-zones`),
          axios.get(`${API}/rentals`),
        ]);
        setCakes(cakesRes.data);
        setDeliveryZones(zonesRes.data);
        setRentals(rentalsRes.data);
        
        // Load rental cart from localStorage
        const savedRentalCart = localStorage.getItem("rentalCart");
        if (savedRentalCart) {
          setRentalCart(JSON.parse(savedRentalCart));
        }
      } catch (error) {
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, []);

  const addToCart = () => {
    if (!selectedCakeToAdd) {
      toast.error("Please select a cake");
      return;
    }
    
    const newItem = {
      id: Date.now(), // unique id for cart item
      cake_id: selectedCakeToAdd.id,
      cake_name: selectedCakeToAdd.name,
      cake_price: selectedCakeToAdd.price,
      cake_image: selectedCakeToAdd.image_url,
      quantity: quantityToAdd,
      cake_message: messageToAdd
    };
    
    setCart([...cart, newItem]);
    setSelectedCakeToAdd(null);
    setQuantityToAdd(1);
    setMessageToAdd("");
    toast.success(`${newItem.cake_name} added to cart!`);
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
    toast.success("Item removed from cart");
  };

  const updateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  // Check loyalty points
  const checkLoyaltyPoints = async () => {
    if (!phone || phone.length < 7) return;
    try {
      const response = await axios.get(`${API}/loyalty/${phone}`);
      setLoyaltyPoints(response.data.points || 0);
      setLoyaltyChecked(true);
      if (response.data.points > 0) {
        toast.success(`You have ${response.data.points} loyalty points!`);
      }
    } catch (error) {
      console.error("Error checking loyalty:", error);
    }
  };

  // Rental cart functions
  const addRentalToCart = (rental) => {
    const existingItem = rentalCart.find(item => item.rental_id === rental.id);
    let newCart;
    
    if (existingItem) {
      newCart = rentalCart.map(item => 
        item.rental_id === rental.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...rentalCart, {
        rental_id: rental.id,
        rental_name: rental.name,
        quantity: 1,
        price_per_day: rental.price_per_day,
        days: 1
      }];
    }
    
    setRentalCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
    toast.success(`${rental.name} added!`);
  };

  const removeRentalFromCart = (rentalId) => {
    const newCart = rentalCart.filter(item => item.rental_id !== rentalId);
    setRentalCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
  };

  const updateRentalQuantity = (rentalId, change) => {
    const newCart = rentalCart.map(item => {
      if (item.rental_id === rentalId) {
        const newQuantity = item.quantity + change;
        if (newQuantity <= 0) return null;
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean);
    
    setRentalCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
  };

  const updateRentalDays = (rentalId, days) => {
    const newCart = rentalCart.map(item => {
      if (item.rental_id === rentalId) {
        return { ...item, days: Math.max(1, days) };
      }
      return item;
    });
    setRentalCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.cake_price * item.quantity), 0);
  };

  const calculateRentalsTotal = () => {
    return rentalCart.reduce((sum, item) => sum + (item.price_per_day * item.quantity * item.days), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const rentalsTotal = calculateRentalsTotal();
    const deliveryFee = selectedZone ? selectedZone.fee : 0;
    return subtotal + rentalsTotal + deliveryFee;
  };

  const handleSubmit = async () => {
    if (cart.length === 0 || !customerName || !phone || !address || !deliveryDate || !deliveryTime || !selectedZone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        customer_name: customerName,
        phone,
        email,
        address,
        items: cart.map(item => ({
          cake_id: item.cake_id,
          cake_name: item.cake_name,
          quantity: item.quantity,
          cake_price: item.cake_price,
          cake_message: item.cake_message || null
        })),
        rentals: rentalCart.map(item => ({
          rental_id: item.rental_id,
          rental_name: item.rental_name,
          quantity: item.quantity,
          price_per_day: item.price_per_day,
          days: item.days
        })),
        special_requests: specialRequests,
        delivery_date: format(deliveryDate, "yyyy-MM-dd"),
        delivery_time: deliveryTime,
        delivery_zone_id: selectedZone.id,
        delivery_zone_name: selectedZone.name,
        delivery_fee: selectedZone.fee,
        payment_method: paymentMethod,
      };

      const response = await axios.post(`${API}/orders`, orderData);
      // Clear rental cart after successful order
      localStorage.removeItem("rentalCart");
      toast.success("Order placed successfully!");
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return cart.length > 0;
      case 2:
        return customerName && phone && address;
      case 3:
        return deliveryDate && deliveryTime && selectedZone;
      case 4:
        return paymentMethod;
      default:
        return false;
    }
  };

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Cake className="w-10 h-10 text-primary" />
            <span className="font-heading text-3xl font-semibold text-foreground tracking-tight">Sis Carol</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              <span className="font-medium text-primary">{cart.length} items</span>
            </div>
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-accent text-2xl text-primary mb-2">Place Your Order</p>
            <h1 className="font-heading text-4xl md:text-5xl text-foreground">Build Your Order</h1>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <button
                    onClick={() => s < step && setStep(s)}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all",
                      step === s ? "bg-primary text-white" : 
                      step > s ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                    )}
                    data-testid={`step-${s}-btn`}
                  >
                    {step > s ? <Check className="w-5 h-5" /> : s}
                  </button>
                  {s < 4 && (
                    <div className={cn(
                      "w-12 md:w-20 h-1 mx-2",
                      step > s ? "bg-primary/20" : "bg-muted"
                    )} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Form Area */}
            <div className="lg:col-span-2">
              {/* Step 1: Select Cakes - Cart System */}
              {step === 1 && (
                <div className="animate-fade-in-up">
                  <h2 className="font-heading text-2xl mb-6">Select Your Cakes</h2>
                  
                  {/* Cake Selection */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {cakes.map((cake, index) => (
                      <Card
                        key={cake.id}
                        data-testid={`cake-select-${index}`}
                        onClick={() => setSelectedCakeToAdd(cake)}
                        className={cn(
                          "cursor-pointer overflow-hidden rounded-2xl transition-all hover:shadow-lg",
                          selectedCakeToAdd?.id === cake.id && "ring-2 ring-primary ring-offset-2"
                        )}
                      >
                        <div className="aspect-video overflow-hidden">
                          <img src={cake.image_url} alt={cake.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-lg font-semibold">{cake.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{cake.description}</p>
                          <p className="font-heading text-xl text-primary">J${cake.price.toLocaleString()}</p>
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Add to Cart Section */}
                  {selectedCakeToAdd && (
                    <div className="bg-white p-6 rounded-2xl border border-border mb-8">
                      <h3 className="font-heading text-lg mb-4">Add {selectedCakeToAdd.name} to Cart</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Quantity</Label>
                          <div className="flex items-center gap-4 mt-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setQuantityToAdd(Math.max(1, quantityToAdd - 1))}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="font-heading text-2xl w-12 text-center">{quantityToAdd}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setQuantityToAdd(quantityToAdd + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label>Message on Cake (Optional)</Label>
                          <Input
                            value={messageToAdd}
                            onChange={(e) => setMessageToAdd(e.target.value)}
                            placeholder="e.g., Happy Birthday!"
                            className="mt-2"
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 mt-4">
                        <Button
                          onClick={addToCart}
                          className="flex-1 rounded-full bg-primary text-white"
                          data-testid="add-to-cart-btn"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add to Cart - J${(selectedCakeToAdd.price * quantityToAdd).toLocaleString()}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setSelectedCakeToAdd(null)}
                          className="rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Cart Items */}
                  {cart.length > 0 && (
                    <div className="bg-secondary/50 p-6 rounded-2xl">
                      <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-primary" />
                        Your Cart ({cart.length} items)
                      </h3>
                      <div className="space-y-4">
                        {cart.map((item) => (
                          <div key={item.id} className="flex items-center gap-4 bg-white p-4 rounded-xl">
                            <img src={item.cake_image} alt={item.cake_name} className="w-16 h-16 rounded-lg object-cover" />
                            <div className="flex-1">
                              <p className="font-medium">{item.cake_name}</p>
                              {item.cake_message && <p className="text-sm text-muted-foreground">"{item.cake_message}"</p>}
                              <p className="text-primary font-heading">J${item.cake_price.toLocaleString()} each</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center font-medium">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="font-heading text-lg w-24 text-right">
                              J${(item.cake_price * item.quantity).toLocaleString()}
                            </p>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFromCart(item.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Party Rentals Section */}
                  <div className="mt-8 border-t pt-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-xl flex items-center gap-2">
                        <PartyPopper className="w-6 h-6 text-purple-500" />
                        Add Party Rentals (Optional)
                      </h3>
                      <Link to="/rentals">
                        <Button variant="outline" size="sm">View All Rentals</Button>
                      </Link>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4">Tables, chairs, tents, decorations, sound systems & more!</p>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {rentals.slice(0, 6).map((rental) => {
                        const inCart = rentalCart.find(item => item.rental_id === rental.id);
                        return (
                          <Card key={rental.id} className="p-3 flex items-center gap-3">
                            <div className="text-2xl">
                              {rental.category === 'furniture' ? '🪑' : 
                               rental.category === 'shelter' ? '⛺' :
                               rental.category === 'decorations' ? '🎈' :
                               rental.category === 'audio' ? '🔊' :
                               rental.category === 'serving' ? '🍽️' : '🎪'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{rental.name}</p>
                              <p className="text-primary text-sm">J${rental.price_per_day.toLocaleString()}/day</p>
                            </div>
                            {inCart ? (
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateRentalQuantity(rental.id, -1)}>
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className="w-6 text-center text-sm font-medium">{inCart.quantity}</span>
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateRentalQuantity(rental.id, 1)}>
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" onClick={() => addRentalToCart(rental)} className="bg-purple-500 hover:bg-purple-600">
                                <Plus className="w-3 h-3" />
                              </Button>
                            )}
                          </Card>
                        );
                      })}
                    </div>

                    {/* Rental Cart Summary */}
                    {rentalCart.length > 0 && (
                      <div className="mt-4 bg-purple-50 p-4 rounded-xl">
                        <h4 className="font-medium text-purple-800 mb-2">Rental Items:</h4>
                        <div className="space-y-2">
                          {rentalCart.map(item => (
                            <div key={item.rental_id} className="flex items-center justify-between bg-white p-2 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{item.rental_name}</span>
                                <span className="text-sm text-muted-foreground">x{item.quantity}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-purple-600">Days:</label>
                                <select 
                                  value={item.days || 1}
                                  onChange={(e) => updateRentalDays(item.rental_id, parseInt(e.target.value))}
                                  className="border rounded px-2 py-1 text-sm w-16"
                                >
                                  {[1,2,3,4,5,6,7].map(d => (
                                    <option key={d} value={d}>{d}</option>
                                  ))}
                                </select>
                                <button onClick={() => removeRentalFromCart(item.rental_id)} className="text-red-500 hover:text-red-700 ml-2">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-sm text-purple-700 mt-3">Rentals Total: <span className="font-bold">J${calculateRentalsTotal().toLocaleString()}</span></p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Customer Details */}
              {step === 2 && (
                <div className="animate-fade-in-up">
                  <h2 className="font-heading text-2xl mb-6">Your Details</h2>
                  <div className="space-y-6 bg-white p-6 md:p-8 rounded-2xl border border-border">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          data-testid="customer-name-input"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Enter your name"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <div className="flex gap-2 mt-2">
                          <Input
                            id="phone"
                            data-testid="customer-phone-input"
                            value={phone}
                            onChange={(e) => { setPhone(e.target.value); setLoyaltyChecked(false); }}
                            placeholder="1876-000-0000"
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={checkLoyaltyPoints}
                            className="whitespace-nowrap"
                          >
                            Check Points
                          </Button>
                        </div>
                        {loyaltyChecked && (
                          <div className={`mt-2 p-2 rounded-lg text-sm ${loyaltyPoints > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {loyaltyPoints > 0 
                              ? `🎉 You have ${loyaltyPoints} loyalty points! (100 pts = J$500 off)`
                              : "No loyalty points yet. Order to earn points!"}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email (Optional)</Label>
                      <Input
                        id="email"
                        data-testid="customer-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Delivery Address *</Label>
                      <Textarea
                        id="address"
                        data-testid="customer-address-input"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Enter your full delivery address"
                        className="mt-2"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                      <Textarea
                        id="specialRequests"
                        data-testid="special-requests-input"
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        placeholder="Any allergies, specific decorations, etc."
                        className="mt-2"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Delivery Details */}
              {step === 3 && (
                <div className="animate-fade-in-up">
                  <h2 className="font-heading text-2xl mb-6">Delivery Details</h2>
                  <div className="space-y-6 bg-white p-6 md:p-8 rounded-2xl border border-border">
                    <div>
                      <Label className="flex items-center gap-2 mb-4">
                        <CalendarIcon className="w-4 h-4" /> Delivery Date *
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="delivery-date-btn"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !deliveryDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {deliveryDate ? format(deliveryDate, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={deliveryDate}
                            onSelect={setDeliveryDate}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <Label className="flex items-center gap-2 mb-4">
                        <Clock className="w-4 h-4" /> Delivery Time *
                      </Label>
                      <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                        <SelectTrigger data-testid="delivery-time-select">
                          <SelectValue placeholder="Select time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="flex items-center gap-2 mb-4">
                        <MapPin className="w-4 h-4" /> Delivery Zone *
                      </Label>
                      <RadioGroup value={selectedZone?.id} onValueChange={(id) => setSelectedZone(deliveryZones.find(z => z.id === id))}>
                        <div className="space-y-3">
                          {deliveryZones.map((zone, index) => (
                            <div
                              key={zone.id}
                              data-testid={`delivery-zone-${index}`}
                              className={cn(
                                "flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all",
                                selectedZone?.id === zone.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                              )}
                              onClick={() => setSelectedZone(zone)}
                            >
                              <div className="flex items-center gap-3">
                                <RadioGroupItem value={zone.id} id={zone.id} />
                                <div>
                                  <Label htmlFor={zone.id} className="font-medium cursor-pointer">{zone.name}</Label>
                                  <p className="text-sm text-muted-foreground">{zone.description}</p>
                                </div>
                              </div>
                              <span className="font-heading text-lg text-primary">
                                {zone.fee === 0 ? "Free" : `J$${zone.fee.toLocaleString()}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Payment Method */}
              {step === 4 && (
                <div className="animate-fade-in-up">
                  <h2 className="font-heading text-2xl mb-6">Payment Method</h2>
                  <div className="space-y-6 bg-white p-6 md:p-8 rounded-2xl border border-border">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                      <p className="text-green-800 font-medium">No payment required now!</p>
                      <p className="text-green-700 text-sm mt-1">Pay when you receive your cake - either on delivery or pickup.</p>
                    </div>
                    
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="space-y-4">
                        <div
                          data-testid="payment-cash"
                          className={cn(
                            "flex items-start gap-4 p-6 rounded-xl border cursor-pointer transition-all",
                            paymentMethod === "cash" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          )}
                          onClick={() => setPaymentMethod("cash")}
                        >
                          <RadioGroupItem value="cash" id="cash" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Banknote className="w-5 h-5 text-primary" />
                              <Label htmlFor="cash" className="font-heading text-lg cursor-pointer">Cash on Delivery/Pickup</Label>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              Pay in cash when your cake is delivered or when you pick it up.
                            </p>
                          </div>
                        </div>

                        <div
                          data-testid="payment-bank-transfer"
                          className={cn(
                            "flex items-start gap-4 p-6 rounded-xl border cursor-pointer transition-all",
                            paymentMethod === "bank_transfer" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          )}
                          onClick={() => setPaymentMethod("bank_transfer")}
                        >
                          <RadioGroupItem value="bank_transfer" id="bank_transfer" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-5 h-5 text-primary" />
                              <Label htmlFor="bank_transfer" className="font-heading text-lg cursor-pointer">Bank Transfer</Label>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              Transfer payment before delivery. Bank details shown after order.
                            </p>
                          </div>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  disabled={step === 1}
                  className="rounded-full px-6"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                {step < 4 ? (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={!canProceed()}
                    className="rounded-full px-6 bg-primary text-primary-foreground hover:opacity-90"
                    data-testid="next-step-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={loading || !canProceed()}
                    className="rounded-full px-8 bg-primary text-primary-foreground hover:opacity-90"
                    data-testid="place-order-btn"
                  >
                    {loading ? "Placing Order..." : "Place Order"}
                  </Button>
                )}
              </div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 order-summary">
                <h3 className="font-heading text-xl mb-6">Order Summary</h3>
                
                {cart.length > 0 ? (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <div className="w-16 h-16 rounded-xl overflow-hidden">
                          <img src={item.cake_image} alt={item.cake_name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.cake_name}</p>
                          <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          <p className="text-primary font-heading text-sm">J${(item.cake_price * item.quantity).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}

                    <div className="border-t border-border pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Cakes Subtotal</span>
                        <span>J${calculateSubtotal().toLocaleString()}</span>
                      </div>
                      {rentalCart.length > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Rentals</span>
                          <span>J${calculateRentalsTotal().toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Delivery</span>
                        <span>{selectedZone ? (selectedZone.fee === 0 ? "Free" : `J$${selectedZone.fee.toLocaleString()}`) : "-"}</span>
                      </div>
                    </div>

                    {/* Rental items in summary */}
                    {rentalCart.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium mb-2 flex items-center gap-1">
                          <PartyPopper className="w-4 h-4 text-purple-500" /> Party Rentals
                        </p>
                        {rentalCart.map(item => (
                          <div key={item.rental_id} className="text-xs text-muted-foreground flex justify-between">
                            <span>{item.rental_name} x{item.quantity}</span>
                            <span>J${(item.price_per_day * item.quantity).toLocaleString()}/day</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between">
                        <span className="font-heading text-lg">Total</span>
                        <span className="font-heading text-2xl text-primary" data-testid="order-total">J${calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>

                    {customerName && (
                      <div className="border-t border-border pt-4 text-sm space-y-1">
                        <p className="font-medium">Delivery to:</p>
                        <p className="text-muted-foreground">{customerName}</p>
                        <p className="text-muted-foreground">{phone}</p>
                        {address && <p className="text-muted-foreground line-clamp-2">{address}</p>}
                      </div>
                    )}

                    {deliveryDate && deliveryTime && (
                      <div className="border-t border-border pt-4 text-sm space-y-1">
                        <p className="font-medium">Delivery on:</p>
                        <p className="text-muted-foreground">{format(deliveryDate, "PPP")}</p>
                        <p className="text-muted-foreground">{deliveryTime}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">Add cakes to your cart</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderPage;
